import { useEffect, useRef, useState } from "react";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Paperclip, Upload, Trash2, FileText, Download, X } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

const TYPE_LABELS = {
  "application/pdf": "PDF",
  "image/jpeg": "JPG",
  "image/png": "PNG",
  "image/webp": "WebP",
};

const fmtBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const canManage = (role) => ["admin", "manager", "super_admin", "owner"].includes(role);
const ADMIN_ROLES = new Set(["admin", "manager", "super_admin"]);

// ENH-017 (Feb 2026 revised): trip_note uploads/deletes are now attendance-
// based, not role-only. The parent (TripDetail) computes `isAttending` and
// passes it in; there's no role gate table for trip_note anymore. Other entity
// types keep the default `canManage` logic.
const ENTITY_TYPE_ROLE_GATES = {};

export default function AttachmentsPanel({
  targetType = "horse",
  targetId,
  testid = "attachments-panel",
  entityType,
  contextLabel,
  isAttending,
}) {
  const { user } = useAuth();
  const fileRef = useRef(null);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [adding, setAdding] = useState(false);
  const [pickedFile, setPickedFile] = useState(null);
  const [description, setDescription] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);

  // Backwards-compat: callers used to pass targetType="horse" and get the
  // "horse_attachment" collection. New callers pass entityType directly.
  const et = entityType || (targetType === "horse" ? "horse_attachment" : targetType);
  const errorScope = contextLabel || (et === "trip_note" ? "trips" : "horses");

  const load = async () => {
    try {
      const { data } = await api.get("/attachments", {
        params: { entity_type: et, entity_id: targetId },
      });
      setItems(data);
    } catch (e) {
      setError(formatApiError(e));
      setItems([]);
    }
  };

  useEffect(() => { if (targetId) load(); }, [targetId, et]);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setPickedFile(f);
    setError("");
  };

  const upload = async (e) => {
    e.preventDefault();
    if (!pickedFile) return;
    setAdding(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", pickedFile);
      fd.append("entity_type", et);
      fd.append("entity_id", targetId);
      if (description) fd.append("description", description);
      await api.post("/attachments", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPickedFile(null);
      setDescription("");
      await load();
    } catch (err) {
      if (err?.response?.status === 403) {
        setError(`You can only manage attachments on your own ${errorScope}.`);
      } else {
        setError(formatApiError(err));
      }
    } finally {
      setAdding(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/attachments/${confirmDel.id}`);
      setConfirmDel(null);
      await load();
    } catch (err) {
      if (err?.response?.status === 403) {
        setError(`You can only manage attachments on your own ${errorScope}.`);
      } else {
        setError(formatApiError(err));
      }
      setConfirmDel(null);
    }
  };

  const fullUrl = (rel) => (rel?.startsWith("http") ? rel : `${BACKEND_URL}${rel}`);
  const role = user?.role;
  // ENH-017 revised: trip_note upload gate is now attendance-based. Parent
  // passes isAttending. Delete permission is decided per-row below.
  const isTripNote = et === "trip_note";
  const canUpload = isTripNote ? Boolean(isAttending) : canManage(role);
  const manage = canUpload;
  const canDeleteAttachment = (a) => {
    if (isTripNote) {
      if (ADMIN_ROLES.has(role)) return true;
      return Boolean(isAttending) && a?.uploaded_by === user?.id;
    }
    return canManage(role);
  };

  return (
    <div className="se-card" data-testid={testid}>
      <div className="mb-3">
        <div className="se-label flex items-center gap-2"><Paperclip size={12} /> Attachments</div>
        <h3 className="font-heading mt-1" style={{ fontSize: 18 }}>Documents & paperwork</h3>
        <p className="text-xs mt-1" style={{ color: "#8C9E8A" }}>
          PDF, JPG, PNG, DOCX, XLSX · max 10 MB per file
        </p>
      </div>

      {manage && (
        <form onSubmit={upload} className="mb-4" data-testid={`${testid}-form`}>
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,application/pdf"
            onChange={onPick}
            style={{ display: "none" }}
            data-testid={`${testid}-input`}
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
            <button type="button" className="se-btn se-btn-ghost" onClick={() => fileRef.current?.click()} data-testid={`${testid}-pick`}>
              <Upload size={14} /> {pickedFile ? pickedFile.name : "Choose file"}
            </button>
            <input
              className="se-input md:col-span-2"
              placeholder="Description (optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              data-testid={`${testid}-description`}
            />
          </div>
          {pickedFile && (
            <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "#8C9E8A" }}>
              <span>{pickedFile.type || "—"} · {fmtBytes(pickedFile.size)}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  className="se-btn se-btn-ghost"
                  style={{ padding: "4px 8px", fontSize: 12 }}
                  onClick={() => { setPickedFile(null); setDescription(""); }}
                  data-testid={`${testid}-clear`}
                >
                  <X size={12} /> Clear
                </button>
                <button type="submit" className="se-btn" style={{ padding: "6px 12px", fontSize: 12 }} disabled={adding} data-testid={`${testid}-submit`}>
                  {adding ? "Uploading…" : "Upload"}
                </button>
              </div>
            </div>
          )}
          {error && (
            <div className="mt-2 text-xs px-2 py-1 rounded" style={{ background: "rgba(192,57,43,0.1)", color: "#922B21" }} data-testid={`${testid}-error`}>
              {error}
            </div>
          )}
        </form>
      )}

      <div className="space-y-2" data-testid={`${testid}-list`}>
        {items === null && <div className="text-sm" style={{ color: "#8C9E8A" }}>Loading…</div>}
        {items !== null && items.length === 0 && (
          <div className="text-sm" style={{ color: "#8C9E8A" }} data-testid={`${testid}-empty`}>
            No attachments yet.
          </div>
        )}
        {(items || []).map((a) => (
          <div
            key={a.id}
            className="flex items-start gap-3 p-3 rounded-lg"
            style={{ background: "rgba(212,169,106,0.06)", border: "0.5px solid rgba(212,169,106,0.2)" }}
            data-testid={`${testid}-item-${a.id}`}
          >
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
              style={{ background: "rgba(124,92,58,0.15)", color: "#7C5C3A" }}
            >
              <FileText size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{a.filename}</div>
              <div className="text-xs mt-0.5" style={{ color: "#8C9E8A" }}>
                {TYPE_LABELS[a.file_type] || a.file_type || "file"} · {fmtBytes(a.size)} · {new Date(a.created_at).toLocaleString()} · {a.uploader_name}
              </div>
              {a.description && (
                <div className="text-sm mt-1 whitespace-pre-wrap">{a.description}</div>
              )}
            </div>
            <div className="flex gap-2">
              <a
                href={fullUrl(a.url)}
                target="_blank"
                rel="noreferrer"
                download={a.filename}
                className="se-btn se-btn-ghost"
                style={{ padding: "6px 10px", fontSize: 12, textDecoration: "none" }}
                data-testid={`${testid}-download-${a.id}`}
              >
                <Download size={12} /> Download
              </a>
              {canDeleteAttachment(a) && (
                <button
                  className="se-btn se-btn-ghost"
                  style={{ padding: "6px 10px", fontSize: 12 }}
                  onClick={() => setConfirmDel(a)}
                  data-testid={`${testid}-delete-${a.id}`}
                  aria-label="Delete attachment"
                >
                  <Trash2 size={12} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <ConfirmDialog
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={remove}
        title="Delete this attachment?"
        description={confirmDel ? `"${confirmDel.filename}" will be removed.` : ""}
        confirmLabel="Yes, delete"
        testid={`${testid}-confirm`}
      />
    </div>
  );
}
